import { useMemo } from 'react';
import compact from 'lodash/compact';
import { EDGE_TYPE } from './directionKeys.tsx';
import { type DiagramTransition, type SelectedTransitionInfo, toPortKey } from './toPortInfo.tsx';
import type { ItemReference } from './ListEdgeDetails.tsx';
import { isDirectOrSharedTransitionWithDefinedPorts } from './isEveryPropertyDefined.tsx';

export interface Options {
	transitions: DiagramTransition[];
	selectedItem: ItemReference | undefined;
}

/** Decorates the currently selected transition (undefined if none selected) with
 *  references to the source/target Port keys.
 */
export const useSelectedTransitionInfo = ({
	transitions,
	selectedItem,
}: Options): SelectedTransitionInfo | undefined => {
	const selectedTransitionInfo = useMemo((): SelectedTransitionInfo | undefined => {
		if (!selectedItem || selectedItem.type !== EDGE_TYPE) return undefined;

		const selectedTransition = transitions.find((x) => x.id === selectedItem.id);

		if (!selectedTransition) return undefined;

		let sourcePortIndexKeys: string[] = [];

		if (isDirectOrSharedTransitionWithDefinedPorts(selectedTransition)) {
			if (selectedTransition.type === 'direct') {
				sourcePortIndexKeys = compact([
					toPortKey(selectedTransition.source, selectedTransition.sourceIndex),
				]);
			} else if (selectedTransition.type === 'shared') {
				sourcePortIndexKeys = compact(
					selectedTransition.links.map(({ source, sourceIndex }) => toPortKey(source, sourceIndex)),
				);
			}
		}

		let targetPortIndexKeys: string[] = [];

		if (isDirectOrSharedTransitionWithDefinedPorts(selectedTransition)) {
			if (selectedTransition.type === 'direct') {
				targetPortIndexKeys = [
					toPortKey(selectedTransition.target, selectedTransition.targetIndex),
				];
			} else if (selectedTransition.type === 'shared') {
				targetPortIndexKeys = selectedTransition.links.map((x) =>
					toPortKey(selectedTransition.target, x.targetIndex),
				);
			}
		}

		return {
			transition: selectedTransition,
			sourcePortIndexKeys,
			targetPortIndexKeys,
		};
	}, [selectedItem, transitions]);

	return selectedTransitionInfo;
};

export default useSelectedTransitionInfo;
